/**
 * Draw a pack link's site insights ABOVE the team offer they arrived after, so the offer stays last.
 *
 * A prepared pack chat is a greeting, then the band offer card (`metadata.bandOfferCard`). When the owner
 * opens a Wix pack link, the harness appends one more ASSISTANT row with the site's insights, tagged
 * `metadata.entryInsights`. It is stored AFTER the card, so the chat opens scrolled to the bottom with the
 * card pushed off the top: the reader sees numbers about their site and not the offer they came to pick.
 *
 * Display order only, and a pure function of the list, so history, the live push and a reload agree. The
 * stored rows, their ids and what the model reads are untouched.
 *
 * Deliberately narrow:
 *  - only a row tagged `entryInsights` moves, and only when an offer card sits EARLIER in the same list.
 *    With no offer before it, it stays where it was written.
 *  - it moves directly above the NEAREST offer card before it — the one it follows — never an older one.
 *
 * No runtime imports, so the server (`/api/conversations/[id]/messages`), the client (`use-chat`) and a
 * unit test can all read it without dragging the app's dependency graph (see CLAUDE.md → Tests).
 */

/** The minimal row shape the reorder reads: the insights tag and the offer card marker. */
export type EntryInsightsOrderRow = {
  entryInsights?: boolean;
  bandOfferCard?: unknown;
};

/**
 * The tag off a row's metadata. Tolerant of the `"true"` string form and of metadata that arrives as a
 * JSON-encoded string, the same tolerance as `readSystemErrorFlag` (lib/failed-turn.ts).
 */
export function readEntryInsightsFlag(metadata: unknown): boolean {
  if (typeof metadata === 'string') {
    try {
      metadata = JSON.parse(metadata);
    } catch {
      return false;
    }
  }
  if (!metadata || typeof metadata !== 'object') {
    return false;
  }
  const value = (metadata as { entryInsights?: unknown }).entryInsights;
  return value === true || (typeof value === 'string' && value.toLowerCase() === 'true');
}

function isOfferCardRow(row: EntryInsightsOrderRow): boolean {
  return Boolean(row.bandOfferCard);
}

function isMovableInsightsRow(row: EntryInsightsOrderRow): boolean {
  return Boolean(row.entryInsights) && !isOfferCardRow(row);
}

function lastOfferCardIndex(rows: EntryInsightsOrderRow[]): number {
  for (let index = rows.length - 1; index >= 0; index -= 1) {
    if (isOfferCardRow(rows[index])) {
      return index;
    }
  }
  return -1;
}

/**
 * Move every `entryInsights` row directly above the nearest offer card before it. Returns the input array
 * unchanged (same identity) when nothing moves, so the common case costs one scan.
 *
 * @example
 * entryInsightsAboveOfferCard([greeting, offer, insights]);
 * // => [greeting, insights, offer]
 *
 * @example
 * entryInsightsAboveOfferCard([greeting, insights]); // => the same array, untouched
 */
export function entryInsightsAboveOfferCard<M extends EntryInsightsOrderRow>(messages: M[]): M[] {
  const firstOffer = messages.findIndex(isOfferCardRow);
  const moves = firstOffer >= 0 && messages.some((row, index) => index > firstOffer && isMovableInsightsRow(row));
  if (!moves) {
    return messages;
  }

  const out: M[] = [];
  for (const row of messages) {
    const offerAt = isMovableInsightsRow(row) ? lastOfferCardIndex(out) : -1;
    if (offerAt < 0) {
      out.push(row);
    } else {
      out.splice(offerAt, 0, row);
    }
  }
  return out;
}
